import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    function handleScroll() {
      setScrolled(window.scrollY > 20);
    }
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled
        ? 'bg-background-50 shadow-sm border-b border-background-200'
        : 'bg-background-50/90 backdrop-blur-md border-b border-background-200/40'
    }`}>
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2 whitespace-nowrap cursor-pointer flex-shrink-0">
            <span className="font-heading text-xl font-bold text-foreground-900 tracking-tight">
              hoobuy<span className="text-primary-500">.website</span>
            </span>
          </a>

          {/* Navigation Links - Desktop */}
          <div className="hidden md:flex items-center gap-2">
            {/* Home */}
            <a
              href="/"
              className="flex items-center px-4 py-2.5 rounded-full text-sm font-medium text-foreground-700 cursor-pointer whitespace-nowrap hover:bg-background-200 transition-colors duration-200"
            >
              <i className="ri-home-4-line w-4 h-4 flex items-center justify-center mr-1.5"></i>
              Início
            </a>

            {/* Guia de Compras */}
            <a
              href="/guia-de-compras"
              className="flex items-center px-4 py-2.5 rounded-full text-sm font-medium text-foreground-700 cursor-pointer whitespace-nowrap hover:bg-background-200 transition-colors duration-200"
            >
              <i className="ri-book-open-line w-4 h-4 flex items-center justify-center mr-1.5"></i>
              Guia de Compras
            </a>

            {/* Category Dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-primary-600 transition-colors duration-200"
              >
                <span>Categorias</span>
                {isOpen ? (
                  <i className="ri-arrow-up-s-line text-sm w-4 h-4 flex items-center justify-center"></i>
                ) : (
                  <i className="ri-arrow-down-s-line text-sm w-4 h-4 flex items-center justify-center"></i>
                )}
              </button>

              {isOpen && (
                <div className="absolute top-full mt-2 right-0 w-72 bg-background-50 border border-background-200 rounded-2xl shadow-lg overflow-hidden z-50">
                  <div className="py-2 max-h-[420px] overflow-y-auto">
                    {categories.map((cat) => (
                      <a
                        key={cat.id}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-100 hover:text-foreground-900 transition-colors duration-150 cursor-pointer whitespace-nowrap"
                        onClick={() => setIsOpen(false)}
                      >
                        <span className="w-2 h-2 rounded-full bg-primary-400 flex-shrink-0"></span>
                        <span>{cat.namePt}</span>
                        <span className="text-xs text-foreground-400 ml-auto">{cat.name}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* CTA Buttons */}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold cursor-pointer whitespace-nowrap hover:bg-primary-600 transition-colors duration-200"
            >
              hoobuy sheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center px-5 py-2.5 rounded-full border-2 border-primary-500 text-primary-600 text-sm font-semibold cursor-pointer whitespace-nowrap hover:bg-primary-50 transition-colors duration-200"
            >
              buy it now
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden flex items-center justify-center w-10 h-10 rounded-full bg-background-200 cursor-pointer transition-colors hover:bg-background-300"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? (
              <i className="ri-close-line text-xl text-foreground-900 w-6 h-6 flex items-center justify-center"></i>
            ) : (
              <i className="ri-menu-line text-xl text-foreground-900 w-6 h-6 flex items-center justify-center"></i>
            )}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50">
          <div className="px-4 py-3 space-y-1 max-h-[70vh] overflow-y-auto">
            {/* Mobile Nav Links */}
            <a
              href="/"
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-home-4-line w-5 h-5 flex items-center justify-center text-primary-500"></i>
              Início
            </a>
            <a
              href="/guia-de-compras"
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-book-open-line w-5 h-5 flex items-center justify-center text-primary-500"></i>
              Guia de Compras
            </a>

            <div className="pt-2 pb-1">
              <span className="px-4 text-xs font-semibold text-foreground-400 uppercase tracking-wider">Categorias</span>
            </div>

            {categories.map((cat) => (
              <a
                key={cat.id}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-foreground-700 hover:bg-accent-100 transition-colors cursor-pointer"
                onClick={() => setMobileOpen(false)}
              >
                <span className="w-2.5 h-2.5 rounded-full bg-accent-500 flex-shrink-0"></span>
                <span className="font-medium">{cat.namePt}</span>
                <span className="text-xs text-foreground-400 ml-auto">{cat.name}</span>
              </a>
            ))}

            <div className="pt-4 flex flex-col gap-2 border-t border-background-200 mt-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold cursor-pointer whitespace-nowrap"
                onClick={() => setMobileOpen(false)}
              >
                <i className="ri-external-link-line w-4 h-4 flex items-center justify-center"></i>
                hoobuy sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 px-5 py-3 rounded-full border-2 border-primary-500 text-primary-600 text-sm font-semibold cursor-pointer whitespace-nowrap"
                onClick={() => setMobileOpen(false)}
              >
                <i className="ri-shopping-cart-line w-4 h-4 flex items-center justify-center"></i>
                buy it now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}